import React, {useMemo, useState} from 'react';
import Styled from 'styled-components';
import moment from 'moment';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {
	Accordion,
	Modal,
	TextField,
	FlexColumn,
	FlexRow,
	FlexItem,
	Label,
	Text,
	DropdownList,
	ButtonDropdownField,
	DateRangePicker,
	IconTooltip,
	Toggle,
	Checkbox,
	SegmentedControl,
	RadioGroupHandler,
	Alert,
} from '@forecast-it/design-system';
import {ForecastQueryRenderer} from '../../../../../ForecastQueryRenderer';
import {createToast} from '../../toasts/toast';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {showModal, MODAL_TYPE} from '../generic_modal_conductor';
import {ClientPrimaryContactDropdown} from '../../dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import Util from '../../../util/util';
import ValidateProjectIdMutation from '../../../../../mutations/ValidateProjectIdMutation';
import CreateProjectMutation from '../../../../../mutations/create_project_mutation';
import DuplicateProjectMutation from '../../../../../mutations/duplicate_project_mutation';
import {projectUrl} from '../../../../../directApi';
import {WinProbabilityStageBands} from '../update-win-chance/WinProbabilityStageBands';
import {toStageBands} from '../../../../opportunities-tab/opportunityStage';
import {hasModule} from '../../../util/ModuleUtil';
import CompanySetupUtil from '../../../util/CompanySetupUtil';
import {MODULE_TYPES, BUDGET_TYPE, PROJECT_STATUS, projectColors} from '../../../../../constants';

// The fields of the newly created project the modal needs after a successful create: the global id
// for the host's row highlight, the two id parts that build the project URL, and whether the project
// uses a baseline (which decides the top-nav exit target).
export type CreateOpportunityCreatedNode = {
	id: string;
	companyProjectId: number;
	customProjectId: string | null;
	useBaseline: boolean | null;
};

export type CreateOpportunityEntryPoint = 'top-nav' | 'opportunities-page' | 'client-page';

export type CreateOpportunityModalPayload = {
	clientId?: string;
	entryPoint?: CreateOpportunityEntryPoint; // defaults to 'top-nav'
	// Host callback for the two list entry points. Returning {newRowVisible: false} appends
	// the "View it under Open." prompt to the success toast. When present, the modal never navigates.
	onCreated?: (node: CreateOpportunityCreatedNode) => {newRowVisible: boolean} | void;
};

// The stage picker is a full-row SegmentedControl that shrinks each segment to share the row (its own
// segments ellipsis-truncate). Inside a design-system FlexRow that only works if the field can shrink
// below the control's natural width — but DS flex items keep the default min-width:auto, so without this
// the control forces its full width and overflows the modal. flex + min-width:0 give it a bounded,
// shrinkable width, matching the definite-width context the Update Win Probability modal already has.
const WinProbabilityField = Styled.div`
	flex: 1 1 0;
	min-width: 0;
`;

export const showCreateOpportunityModal = (payload: CreateOpportunityModalPayload = {}) =>
	showModal({type: MODAL_TYPE.CREATE_OPPORTUNITY, ...payload});

// Single-item Accordion that starts open — three of these give "all open, each collapsible"
// without an all-open prop (which the design-system Accordion doesn't have).
const OpportunitySection = ({id, labelId, children}: {id: string; labelId: string; children: React.ReactNode}) => {
	const {formatMessage} = useIntl();
	return (
		<Accordion selectionType={Accordion.SelectionType.SINGLE} collapsible defaultValue={id} background={true} border={true}>
			<Accordion.Item value={id} label={formatMessage({id: labelId})} content="" data-cy={`section-${id}`}>
				{/* The section's own data-cy lands on the accordion trigger, and the trigger's
					aria-controls id is not unique across sibling accordions — so the field area
					carries its own hook for scoping assertions to one section. */}
				<div data-cy={`${id}-fields`}>{children}</div>
			</Accordion.Item>
		</Accordion>
	);
};

type InnerProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the modal's query is untyped, so the viewer shape is not narrowed here
	viewer: any;
	closeModal: () => void;
	// Pre-selects the Client field when the modal is opened from the client Opportunities tab.
	clientId?: string;
	entryPoint?: CreateOpportunityEntryPoint;
	onCreated?: CreateOpportunityModalPayload['onCreated'];
};

type ClientContact = {id: string; name: string} | null;
type PersonOption = {id: string; fullName: string};
type ClientOption = {id: string; name: string};
type RateCardOption = {id: string; name: string; currency: string};

// The design-system DateRangePicker's onChange is invoked with a single interval object
// ({startDate, endDate}, ISO date strings) — not two positional Date args. Mirrors
// new_project_modal_v2.js's onDateRangeChangeIntervalObject. Exported for direct unit testing
// since driving the real react-aria date fields through jsdom is impractical.
export const intervalToMoments = (interval: {
	startDate?: string;
	endDate?: string;
}): {startDate: moment.Moment | null; endDate: moment.Moment | null} => ({
	startDate: interval.startDate ? moment(interval.startDate) : null,
	endDate: interval.endDate ? moment(interval.endDate) : null,
});

// Resolves what the modal's dates become for an interval reported by the picker.
//
// The picker owns the range as a single value, so deleting the start segment collapses the WHOLE
// interval and reports both ends as empty — even when the user only touched the start. A start date
// is required for an opportunity, so that case keeps both dates as they were and asks the caller to
// remount the field (`retainStart`); otherwise an end date the user had already chosen would be lost
// as a side effect of retyping the start. Any interval that still carries a start is applied as-is,
// which is what lets the end date be cleared on its own.
export const resolveDateRangeChange = (
	interval: {startDate?: string; endDate?: string},
	current: {startDate: moment.Moment; endDate: moment.Moment | null}
): {startDate: moment.Moment; endDate: moment.Moment | null; retainStart: boolean} => {
	const {startDate, endDate} = intervalToMoments(interval);
	if (!startDate) {
		return {startDate: current.startDate, endDate: current.endDate, retainStart: true};
	}
	return {startDate, endDate, retainStart: false};
};

// Loaded lazily (only when the Duplicate toggle is on) so opening the modal doesn't fetch the full
// project list. Replaces the legacy DuplicateProjectDropdown (a non-design-system dropdown with an
// uppercase label) — "Project to Duplicate" now uses the same DropdownList + ButtonDropdownField as
// the modal's other pickers.
const duplicateProjectsQuery = graphql`
	query CreateOpportunityModalDuplicateProjects_Query {
		viewer {
			actualPersonId
			component(name: "create_opportunity_duplicate_projects")
			projects(first: 10000) {
				edges {
					node {
						id
						name
						companyProjectId
						customProjectId
						fullAccessToProject
					}
				}
			}
		}
	}
`;

export const CreateOpportunityModalInner = ({viewer, closeModal, clientId, entryPoint, onCreated}: InnerProps) => {
	const {formatMessage} = useIntl();
	const history = useHistory();

	const [name, setName] = useState('');
	const [customProjectId, setCustomProjectId] = useState('');
	const [customProjectIdError, setCustomProjectIdError] = useState<string | undefined>();
	const [salesperson, setSalesperson] = useState<string | null>(null);
	// When opened from the client Opportunities tab, pre-select the current client.
	const [client, setClient] = useState<string | null>(clientId ?? null);
	const [clientContact, setClientContact] = useState<ClientContact>(null);
	const [winChance, setWinChance] = useState(0.5);
	// The account's stage mapping, which the win probability picker is built from.
	const stageBands = useMemo(() => toStageBands(viewer.company.opportunityStageBands), [viewer]);

	// Start date is required for opportunities and defaults to today; end date is optional.
	// The submit assembly reads these via moment's .date()/.month()/.year(), so they're moment
	// objects, not raw Dates — matching ProjectMainInfo/new_project_modal_v2's pattern.
	const [projectStartDate, setProjectStartDate] = useState<moment.Moment>(moment());
	const [projectEndDate, setProjectEndDate] = useState<moment.Moment | null>(null);
	// Remount key used to snap the picker back when a user deletes the start-date segments.
	const [dateFieldKey, setDateFieldKey] = useState(0);
	// Default to a swatch colour so one appears selected on open (the previous #7A55ED wasn't in the palette).
	const [projectColor, setProjectColor] = useState(projectColors[0]);
	const [projectLabels, setProjectLabels] = useState<string[]>([]);
	// Sprints are opt-in; the duration only shows (and is only sent) once Yes is chosen.
	const [useSprints, setUseSprints] = useState(false);
	const [sprintLengthInDays, setSprintLengthInDays] = useState('');
	const [useDuplication, setUseDuplication] = useState(false);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated fragment once the duplicate flow query is wired to a fragment
	const [projectToDuplicate, setProjectToDuplicate] = useState<any>(null);
	const [duplicateSettings, setDuplicateSettings] = useState(true);
	const [duplicateTasks, setDuplicateTasks] = useState(true);
	const [duplicatePhases, setDuplicatePhases] = useState(true);
	const [duplicateTeam, setDuplicateTeam] = useState(true);
	const [duplicateAllocations, setDuplicateAllocations] = useState(true);
	const [duplicateExpenses, setDuplicateExpenses] = useState(true);
	const [duplicateRetainerPeriods, setDuplicateRetainerPeriods] = useState(true);
	const [duplicatePlaceholders, setDuplicatePlaceholders] = useState(true);
	const [duplicateColor, setDuplicateColor] = useState(true);

	// Time & Materials is the default budget type for a new opportunity. Retainer and
	// Non-Billable are rendered but disabled — not selectable yet for opportunities.
	const [budgetType, setBudgetType] = useState<string>(BUDGET_TYPE.TIME_AND_MATERIALS);
	// Price only applies to Fixed Price budgets.
	const [fixedPriceBudget, setFixedPriceBudget] = useState('');
	const rateCards: RateCardOption[] = useMemo(() => viewer.company.rateCards.edges.map(edge => edge.node), [viewer]);
	const [rateCardSelected, setRateCardSelected] = useState<RateCardOption | null>(viewer.company.defaultRateCard ?? null);
	// Use Baseline defaults ON for new opportunities.
	const [useBaseline, setUseBaseline] = useState(true);
	// Guards against double-submit (rapid double-click would otherwise fire two create mutations).
	// Mirrors new_project_modal_v2.js's `creatingProject` state.
	const [creating, setCreating] = useState(false);

	const labels = useMemo(() => viewer.company.labels.edges.map(edge => edge.node), [viewer]);

	// Currency symbol for the Price label (e.g. USD -> "$"), derived from the company currency.
	const currencySymbol = useMemo(() => {
		try {
			return (
				new Intl.NumberFormat(undefined, {style: 'currency', currency: viewer.company.currency})
					.formatToParts(0)
					.find(part => part.type === 'currency')?.value ?? viewer.company.currency
			);
		} catch {
			return viewer.company.currency;
		}
	}, [viewer]);

	const addLabel = (labelId: string) => setProjectLabels(projectLabels.concat([labelId]));
	const removeLabel = (labelIdToRemove: string) => setProjectLabels(projectLabels.filter(id => id !== labelIdToRemove));

	const onDuplicateToggle = (checked: boolean) => {
		setUseDuplication(checked);
		if (!checked) {
			setProjectToDuplicate(null);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- duplicate project node shape comes from the untyped duplicate-projects query
	const handleDuplicateProjectSelectChange = (duplicatedProjectNode: any) => setProjectToDuplicate(duplicatedProjectNode);

	// Memoized so the 10k-row map/sort doesn't re-run on every keystroke (only when `viewer` changes).
	const persons: PersonOption[] = useMemo(
		() => viewer.company.allPersons.edges.map(edge => edge.node).filter(person => person.active),
		[viewer]
	);
	const clients: ClientOption[] = useMemo(
		() => [...viewer.company.clients.edges.map(edge => edge.node)].sort((a, b) => Util.sortAlphabetically(a.name, b.name)),
		[viewer]
	);
	// Salesperson/Client are plain chevron dropdowns (DropdownList + ButtonDropdownField), not
	// ComboBox — option value is the person/client GLOBAL id so state (`salesperson`/`client`)
	// keeps feeding the submit assembly unchanged.
	const salespersonOptions = useMemo(() => persons.map(person => ({value: person.id, label: person.fullName})), [persons]);
	const clientOptions = useMemo(() => clients.map(clientNode => ({value: clientNode.id, label: clientNode.name})), [clients]);

	const onCustomProjectIdChange = (value: string) => {
		setCustomProjectId(value);
		setCustomProjectIdError(undefined);
	};

	const onCustomProjectIdBlur = () => {
		if (customProjectId === '') {
			setCustomProjectIdError(undefined);
			return;
		}
		if (!customProjectId.match(/^[a-z0-9-]+$/i)) {
			setCustomProjectIdError(formatMessage({id: 'new_project_modal.project_id_error.alphanumeric'}));
			return;
		}
		Util.CommitMutation(ValidateProjectIdMutation, {projectId: customProjectId}, result => {
			if (result.validateProjectId.valid === 'true') {
				setCustomProjectIdError(undefined);
			} else {
				setCustomProjectIdError(formatMessage({id: 'new_project_modal.project_id_error.already_in_use'}));
			}
		});
	};

	const onClientChange = (selectedClientId: string | null) => {
		// A contact is scoped to its client, so changing the client clears the chosen Contact.
		setClient(selectedClientId);
		setClientContact(null);
	};

	const onDateRangeChange = (interval: {startDate?: string; endDate?: string}) => {
		const next = resolveDateRangeChange(interval, {startDate: projectStartDate, endDate: projectEndDate});
		setProjectStartDate(next.startDate);
		setProjectEndDate(next.endDate);
		if (next.retainStart) {
			// Remount the picker so the field shows the retained values again.
			setDateFieldKey(key => key + 1);
		}
	};

	// Radix single ToggleGroup deselects on re-click (emits ''); ignore empty so a value is always held
	// (the same guard the win-probability band picker applies).
	const onBudgetTypeChange = (value: string) => {
		if (value) {
			setBudgetType(value);
		}
	};

	const onRateCardChange = (rateCardId: string | null) =>
		setRateCardSelected(rateCards.find(rateCard => rateCard.id === rateCardId) ?? null);

	// Shared success epilogue for the create and duplicate paths: analytics, toast,
	// then either the host's in-place refresh (list entry points) or the top-nav navigation.
	const finishCreate = (node: CreateOpportunityCreatedNode, duplicated: boolean) => {
		trackEvent('Opportunity', 'Created', {
			entryPoint: entryPoint ?? 'top-nav',
			duplicated,
			useBaseline: !!node.useBaseline,
			winChance,
			clientSet: !!client,
			salespersonSet: !!salesperson,
		});
		const hostResult = onCreated ? onCreated(node) : undefined;
		const rowHidden = !!hostResult && hostResult.newRowVisible === false;
		const toastText = rowHidden
			? `${formatMessage({id: 'opportunities.created_toast'}, {name})} ${formatMessage({
					id: 'opportunities.created_toast_view_open',
			  })}`
			: formatMessage({id: 'opportunities.created_toast'}, {name});
		createToast({duration: 5000, variant: 'banner', message: <Alert type={Alert.TYPE.SUCCESS} text={toastText} />});
		closeModal();
		// A host that refreshes its own list keeps the user where they are; the top nav has no list
		// to return to, so a baseline-backed opportunity goes straight to its Baseline setup page.
		if (!onCreated && node.useBaseline) {
			history.push(`${projectUrl(node.companyProjectId, node.customProjectId)}/baseline`);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- result shape comes from the untyped CreateProjectMutation
	const onSuccess = (result: any) => {
		// Payload-level errors (e.g. Sage Intacct) arrive here, not onError — and on that path
		// `project` is null, so guard before reading it (mirrors new_project_modal_v2.js).
		if (Util.checkForSageErrorAndShowModal(result.createProject.errors, false)) {
			setCreating(false);
			return;
		}
		finishCreate(result.createProject.project.node, false);
	};

	// Duplicate flow returns the new project under `duplicateProject` (not `createProject`); it finishes
	// exactly like the create path. Mirrors new_project_modal_v2.js's duplicate branch.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- result shape comes from the untyped DuplicateProjectMutation
	const onSuccessDuplicate = (result: any) => {
		if (Util.checkForSageErrorAndShowModal(result.duplicateProject.errors, false)) {
			setCreating(false);
			return;
		}
		finishCreate(result.duplicateProject.project.node, true);
	};

	const handleCreate = () => {
		if (creating) return; // double-submit guard
		// Fields common to both paths. `status` is ALWAYS OPPORTUNITY so a duplicated project lands
		// as an opportunity regardless of the source project's stage (DuplicateProjectInput carries
		// salespersonId/baselineWinChance/status).
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Create/DuplicateProjectInput aren't typed on the JS mutation modules
		const input: any = {
			name,
			customProjectId: customProjectId || undefined,
			salespersonId: salesperson || undefined,
			baselineWinChance: winChance,
			status: PROJECT_STATUS.OPPORTUNITY,
			clientId: client || undefined,
			clientContactId: clientContact?.id,
		};
		// The start date always has a value (defaults to today, cannot be cleared); the end date is
		// optional and only sent when picked.
		input.projectStartDay = projectStartDate.date();
		input.projectStartMonth = projectStartDate.month() + 1;
		input.projectStartYear = projectStartDate.year();
		if (projectEndDate) {
			input.projectEndDay = projectEndDate.date();
			input.projectEndMonth = projectEndDate.month() + 1;
			input.projectEndYear = projectEndDate.year();
		}
		setCreating(true);
		if (useDuplication && projectToDuplicate) {
			// Duplication MUST go through DuplicateProjectMutation — createProject ignores
			// projectId/duplicate* fields. Financial (budget type/price/rate card/baseline), colour and
			// labels are hidden in duplicate mode and inherited from the source project
			// — none of their form data is sent (the mutation also omits labels/rateCardId).
			input.projectId = projectToDuplicate.id;
			input.duplicateSettings = duplicateSettings;
			input.duplicatePhases = duplicatePhases;
			input.duplicateTasks = duplicateTasks;
			input.duplicateProjectPersons = duplicateTeam;
			input.duplicateAllocations = duplicateAllocations;
			input.duplicateExpenses = duplicateExpenses;
			input.duplicateRetainerPeriods = duplicateRetainerPeriods;
			input.duplicatePlaceholders = duplicatePlaceholders;
			input.duplicateColor = duplicateColor;
			Util.CommitMutation(DuplicateProjectMutation, input, onSuccessDuplicate);
			return;
		}
		// Create-from-scratch path: financials (budget type/price), colour and labels are create-only.
		// The Financial and Labels sections are hidden while duplicating and their
		// form data is not sent — a duplicate inherits these from the source project.
		input.budgetType = budgetType;
		// Only send budget when a Fixed Price value parses to a real number — parseFloat('') and
		// parseFloat('.') are NaN, which must not reach the GraphQLFloat field (mirrors
		// new_project_modal_v2.js). The `!isNaN` guard covers a lone "." that the input regex allows.
		if (budgetType === BUDGET_TYPE.FIXED_PRICE && fixedPriceBudget) {
			const parsedBudget = parseFloat(fixedPriceBudget);
			if (!isNaN(parsedBudget)) {
				input.budget = parsedBudget;
			}
		}
		input.useBaseline = useBaseline;
		if (useSprints) {
			input.sprintTimeBox = true;
			// The field only ever holds an empty string or a whole number of at least 1, so a failed
			// parse means the user left it blank and wants the default length.
			const parsedSprintLength = parseInt(sprintLengthInDays, 10);
			input.sprintLengthInDays = isNaN(parsedSprintLength) ? 14 : parsedSprintLength;
		}
		input.rateCardId = rateCardSelected?.id;
		input.projectColor = projectColor;
		input.labels = projectLabels;
		Util.CommitMutation(CreateProjectMutation, input, onSuccess);
	};

	// A partial hex from the manual input (e.g. "#AB") passes the as-you-type regex but the backend
	// rejects it — block submit until it's a full #RRGGBB. Only relevant in create mode (colour is
	// hidden and not sent in duplicate mode).
	const colorInvalid = !useDuplication && !/^#[0-9A-Fa-f]{6}$/.test(projectColor);

	// Duplicating without a source project has nothing to copy. Submitting would fall through to the
	// create-from-scratch path and build a blank opportunity out of the budget/colour/label defaults
	// the user never saw — those sections are hidden while duplicating — so block it instead.
	const duplicateSourceMissing = useDuplication && !projectToDuplicate;

	return (
		<Modal onCloseModal={closeModal} size={Modal.SIZE.EXTRA_LARGE}>
			{/* Modal.Title (Radix Dialog.Title) is itself the <h2> and already applies heading-3 font —
				don't nest a <Heading> (h3) inside it. */}
			<Modal.Title>{formatMessage({id: 'opportunity.create_title'})}</Modal.Title>
			<Modal.Content data-cy="create-opportunity-modal">
				{/* Three sections, all expanded on mount, each individually collapsible.
					The design-system Accordion's defaultValue opens only ONE item, so use three
					independent single-item Accordions (each defaultValue-open) rather than one. */}
				<OpportunitySection id="overview" labelId="opportunity.section.overview">
					<FlexColumn gap="l">
						<FlexRow>
							<TextField
								data-cy="opportunity-name"
								label={formatMessage({id: 'opportunity.field.name'})}
								value={name}
								onValueChange={setName}
								maxLength={191}
							/>
						</FlexRow>
						<FlexRow gap="l">
							<FlexItem flex="1 1 0">
								<TextField
									data-cy="opportunity-id"
									label={formatMessage({id: 'opportunity.field.id'})}
									value={customProjectId}
									onValueChange={onCustomProjectIdChange}
									onBlur={onCustomProjectIdBlur}
									hasError={!!customProjectIdError}
									errorText={customProjectIdError}
									maxLength={20}
									placeholder={`P${viewer.company.projectLastSequence + 1}`}
								/>
							</FlexItem>
							{useDuplication ? null : (
								<FlexItem flex="1 1 0">
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'common.color'})}</Label>
										<FlexRow gap="s" alignItems="center">
											{projectColors.slice(0, 6).map(colorOption => (
												<button
													key={colorOption}
													type="button"
													data-cy={`opportunity-color-${colorOption}`}
													aria-label={colorOption}
													aria-pressed={projectColor === colorOption}
													onClick={() => setProjectColor(colorOption)}
													style={{
														width: 32,
														height: 32,
														flex: '0 0 auto',
														borderRadius: 4,
														cursor: 'pointer',
														backgroundColor: colorOption,
														border:
															projectColor === colorOption
																? '2px solid #6C5CE7'
																: '1px solid #E6E7EB',
													}}
												/>
											))}
											<FlexItem flex="1 1 0">
												<TextField
													data-cy="opportunity-color-hex"
													aria-label={formatMessage({id: 'common.color'})}
													value={projectColor}
													placeholder="#000000"
													onValueChange={(value: string) => {
														if (/^#([0-9A-Fa-f]{0,6})$/.test(value)) {
															setProjectColor(value);
														}
													}}
												/>
											</FlexItem>
										</FlexRow>
									</FlexColumn>
								</FlexItem>
							)}
						</FlexRow>
						<FlexRow gap="l">
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<FlexRow gap="xs" alignItems="center">
										<Label>{formatMessage({id: 'opportunity.field.project_dates'})}</Label>
										<IconTooltip>
											<IconTooltip.Item title={formatMessage({id: 'opportunity.dates_tooltip'})} />
										</IconTooltip>
									</FlexRow>
									<DateRangePicker
										key={dateFieldKey}
										data-cy="opportunity-dates"
										aria-label={formatMessage({id: 'opportunity.field.project_dates'})}
										startDate={projectStartDate.format('YYYY-MM-DD')}
										endDate={projectEndDate?.format('YYYY-MM-DD')}
										onChange={onDateRangeChange}
									/>
								</FlexColumn>
							</FlexItem>
							<FlexItem flex="1 1 0">
								{/* Opened from a client's own Opportunities tab the client is fixed, so the field
									is a disabled text field instead of a picker. Both branches expose the same hook. */}
								{clientId ? (
									<div data-cy="opportunity-client">
										<TextField
											label={formatMessage({id: 'common.client'})}
											value={clientOptions.find(option => option.value === client)?.label ?? ''}
											onValueChange={() => undefined}
											disabled
										/>
									</div>
								) : (
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'common.client'})}</Label>
										<DropdownList>
											<DropdownList.Trigger
												data-cy="opportunity-client"
												aria-label={formatMessage({id: 'common.client'})}
											>
												<ButtonDropdownField>
													<Text>
														{clientOptions.find(o => o.value === client)?.label ??
															formatMessage({id: 'common.none'})}
													</Text>
												</ButtonDropdownField>
											</DropdownList.Trigger>
											<DropdownList.Content align="start" useTriggerWidth>
												<DropdownList.Item
													data-cy="opportunity-client-none"
													onClick={() => onClientChange(null)}
												>
													<Text>{formatMessage({id: 'common.none'})}</Text>
												</DropdownList.Item>
												{clientOptions.map(option => (
													<DropdownList.Item
														key={option.value}
														data-cy={`opportunity-client-${option.value}`}
														onClick={() => onClientChange(option.value)}
													>
														<Text>{option.label}</Text>
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										</DropdownList>
									</FlexColumn>
								)}
							</FlexItem>
						</FlexRow>
						<FlexRow gap="l">
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'common.contact'})}</Label>
									{client ? (
										<ClientPrimaryContactDropdown
											key={client}
											clientId={client}
											value={clientContact}
											onChange={setClientContact}
											hideLabel
											showPrimaryChip
											dataCy="opportunity-contact"
										/>
									) : (
										<div data-cy="opportunity-contact">
											<ButtonDropdownField disabled>
												<Text>{formatMessage({id: 'project_settings.select_contact'})}</Text>
											</ButtonDropdownField>
										</div>
									)}
								</FlexColumn>
							</FlexItem>
							<FlexItem flex="1 1 0">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'opportunity.field.salesperson'})}</Label>
									<DropdownList>
										<DropdownList.Trigger
											data-cy="opportunity-salesperson"
											aria-label={formatMessage({id: 'opportunity.field.salesperson'})}
										>
											<ButtonDropdownField>
												<Text>
													{salespersonOptions.find(o => o.value === salesperson)?.label ??
														formatMessage({id: 'common.none'})}
												</Text>
											</ButtonDropdownField>
										</DropdownList.Trigger>
										<DropdownList.Content align="start" useTriggerWidth>
											<DropdownList.Item
												data-cy="opportunity-salesperson-none"
												onClick={() => setSalesperson(null)}
											>
												<Text>{formatMessage({id: 'common.none'})}</Text>
											</DropdownList.Item>
											{salespersonOptions.map(option => (
												<DropdownList.Item
													key={option.value}
													data-cy={`opportunity-salesperson-${option.value}`}
													onClick={() => setSalesperson(option.value)}
												>
													<Text>{option.label}</Text>
												</DropdownList.Item>
											))}
										</DropdownList.Content>
									</DropdownList>
								</FlexColumn>
							</FlexItem>
						</FlexRow>
						<FlexRow>
							<WinProbabilityField>
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'opportunities.update_win_probability.section'})}</Label>
									<WinProbabilityStageBands bands={stageBands} value={winChance} onChange={setWinChance} />
								</FlexColumn>
							</WinProbabilityField>
						</FlexRow>
					</FlexColumn>
				</OpportunitySection>
				{/* Financial — create mode only. Hidden while duplicating; budget type,
					price, rate card and baseline all come from the source project. */}
				{useDuplication ? null : (
					<OpportunitySection id="financial" labelId="opportunity.section.financial">
						<FlexColumn gap="l">
							<FlexRow>
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'opportunity.field.budget_type'})}</Label>
									{/* Only Fixed Price and Time & Materials are selectable for opportunities today;
									Retainer and Non-Billable are shown disabled. */}
									<SegmentedControl
										value={budgetType}
										onValueChange={onBudgetTypeChange}
										data-cy="opportunity-budget-type"
									>
										<SegmentedControl.Item
											value={BUDGET_TYPE.FIXED_PRICE}
											label={formatMessage({id: 'common.fixed_price'})}
											data-cy="budget-FIXED_PRICE"
										>
											{formatMessage({id: 'common.fixed_price'})}
										</SegmentedControl.Item>
										<SegmentedControl.Item
											value={BUDGET_TYPE.TIME_AND_MATERIALS}
											label={formatMessage({id: 'common.time_material'})}
											data-cy="budget-TIME_AND_MATERIALS"
										>
											{formatMessage({id: 'common.time_material'})}
										</SegmentedControl.Item>
										<SegmentedControl.Item
											value={BUDGET_TYPE.RETAINER}
											label={formatMessage({id: 'common.retainer'})}
											disabled
											data-cy="budget-RETAINER"
										>
											{formatMessage({id: 'common.retainer'})}
										</SegmentedControl.Item>
										<SegmentedControl.Item
											value={BUDGET_TYPE.NON_BILLABLE}
											label={formatMessage({id: 'common.non-billable'})}
											disabled
											data-cy="budget-NON_BILLABLE"
										>
											{formatMessage({id: 'common.non-billable'})}
										</SegmentedControl.Item>
									</SegmentedControl>
								</FlexColumn>
							</FlexRow>
							<FlexRow gap="l">
								{budgetType === BUDGET_TYPE.FIXED_PRICE ? (
									<FlexItem flex="1 1 0">
										{/* Design-system TextField (matches the dropdown height + the ID field) — its label
										associates with the input, and it lines up with Rate Card the way ID lines up with
										Salesperson. allowedCharactersRegex allows digits and a single decimal point
										(so "1.2.3" can't be typed); the submit handler still guards a lone ".". */}
										<TextField
											data-cy="opportunity-fixed-price-budget"
											label={formatMessage({id: 'opportunity.field.price'}, {currency: currencySymbol})}
											value={fixedPriceBudget}
											onValueChange={setFixedPriceBudget}
											placeholder="0"
											allowedCharactersRegex="^\d*\.?\d*$"
										/>
									</FlexItem>
								) : null}
								<FlexItem flex="1 1 0">
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'common.rate_card'})}</Label>
										<DropdownList>
											<DropdownList.Trigger
												data-cy="opportunity-rate-card"
												aria-label={formatMessage({id: 'common.rate_card'})}
											>
												<ButtonDropdownField>
													<Text>{rateCardSelected?.name ?? formatMessage({id: 'common.none'})}</Text>
												</ButtonDropdownField>
											</DropdownList.Trigger>
											<DropdownList.Content align="start" useTriggerWidth>
												{rateCards.map(rateCard => (
													<DropdownList.Item
														key={rateCard.id}
														data-cy={`opportunity-rate-card-${rateCard.id}`}
														onClick={() => onRateCardChange(rateCard.id)}
													>
														<Text>{rateCard.name}</Text>
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										</DropdownList>
									</FlexColumn>
								</FlexItem>
							</FlexRow>
							{/* Use Baseline: switch + sibling label in a centred FlexRow so the click target is the toggle,
								not the full-width row, and the switch lines up with the label (mirrors the duplicate toggle). */}
							<FlexRow gap="s" alignItems="center">
								<Text>{formatMessage({id: 'opportunity.field.use_baseline'})}</Text>
								<Toggle checked={useBaseline} onChange={setUseBaseline} cy="opportunity-use-baseline" />
							</FlexRow>
						</FlexColumn>
					</OpportunitySection>
				)}
				<OpportunitySection id="more-settings" labelId="opportunity.section.more_settings">
					<FlexColumn gap="l">
						{/* Labels — full width, create mode only. Hidden while duplicating —
							the copy inherits the source project's labels, so a value picked here is not applied. */}
						{useDuplication ? null : (
							<FlexRow>
								<FlexItem flex="1 1 100%">
									<FlexColumn gap="xs">
										<Label>{formatMessage({id: 'project_settings.project-labels'})}</Label>
										<DropdownList>
											<DropdownList.Trigger
												data-cy="opportunity-labels"
												aria-label={formatMessage({id: 'project_settings.project-labels'})}
											>
												<ButtonDropdownField>
													<Text>
														{projectLabels.length
															? `${formatMessage({id: 'common.added'})} (${projectLabels.length})`
															: formatMessage({id: 'settings_labels.add-label'})}
													</Text>
												</ButtonDropdownField>
											</DropdownList.Trigger>
											<DropdownList.Content align="start" useTriggerWidth>
												{labels.map(labelOption => (
													<DropdownList.CheckboxItem
														key={labelOption.id}
														data-cy={`opportunity-label-${labelOption.id}`}
														checked={projectLabels.includes(labelOption.id)}
														onChange={() =>
															projectLabels.includes(labelOption.id)
																? removeLabel(labelOption.id)
																: addLabel(labelOption.id)
														}
													>
														<FlexRow gap="s" alignItems="center">
															<div
																style={{
																	width: 12,
																	height: 12,
																	borderRadius: 2,
																	backgroundColor: labelOption.color,
																}}
															/>
															<Text>{labelOption.name}</Text>
														</FlexRow>
													</DropdownList.CheckboxItem>
												))}
											</DropdownList.Content>
										</DropdownList>
									</FlexColumn>
								</FlexItem>
							</FlexRow>
						)}
						{/* Sprints — create mode only. A duplicate inherits the source project's sprint setup. */}
						{useDuplication ? null : (
							<FlexRow gap="l" alignItems="flex-end">
								<FlexItem flex="1 1 0">
									{/* Item renders only the radio circle; the option text is a sibling Text in a FlexRow
										(the design-system radio-group pattern used by the expense modals). */}
									<RadioGroupHandler
										label={formatMessage({id: 'opportunity.field.work_with_sprints'})}
										value={useSprints ? 'yes' : 'no'}
										onValueChange={(value: string) => setUseSprints(value === 'yes')}
									>
										<FlexRow gap="l">
											<FlexRow gap="s">
												<RadioGroupHandler.Item value="yes" data-testid="opportunity-sprints-yes" />
												<Text>{formatMessage({id: 'common.yes'})}</Text>
											</FlexRow>
											<FlexRow gap="s">
												<RadioGroupHandler.Item value="no" data-testid="opportunity-sprints-no" />
												<Text>{formatMessage({id: 'common.no'})}</Text>
											</FlexRow>
										</FlexRow>
									</RadioGroupHandler>
								</FlexItem>
								{useSprints ? (
									<FlexItem flex="1 1 0">
										<TextField
											data-cy="opportunity-sprint-length"
											label={formatMessage({id: 'opportunity.field.sprint_duration'})}
											value={sprintLengthInDays}
											onValueChange={(value: string) => {
												// Whole days only, never zero or zero-padded: a 0-day sprint is
												// meaningless, and it would be indistinguishable from "left
												// blank" once the submit falls back to the default length.
												if (value === '' || /^[1-9]\d*$/.test(value)) {
													setSprintLengthInDays(value);
												}
											}}
											placeholder="14"
										/>
									</FlexItem>
								) : null}
							</FlexRow>
						)}
						{/* Same sibling-Text radio pattern as Work with Sprints — Item never renders children. */}
						<RadioGroupHandler
							label={formatMessage({id: 'opportunity.duplicate_existing_project'})}
							value={useDuplication ? 'yes' : 'no'}
							onValueChange={(value: string) => onDuplicateToggle(value === 'yes')}
						>
							<FlexRow gap="l">
								<FlexRow gap="s">
									<RadioGroupHandler.Item value="yes" data-testid="opportunity-duplicate-yes" />
									<Text>{formatMessage({id: 'common.yes'})}</Text>
								</FlexRow>
								<FlexRow gap="s">
									<RadioGroupHandler.Item value="no" data-testid="opportunity-duplicate-no" />
									<Text>{formatMessage({id: 'common.no'})}</Text>
								</FlexRow>
							</FlexRow>
						</RadioGroupHandler>
						{useDuplication ? (
							<FlexColumn gap="l">
								<FlexRow>
									<FlexItem flex="1 1 100%">
										<div data-testid="duplicate-project-dropdown">
											<FlexColumn gap="xs">
												<Label>{formatMessage({id: 'opportunity.project_to_duplicate'})}</Label>
												<ForecastQueryRenderer
													key="query-render-duplicate-projects"
													query={duplicateProjectsQuery}
													customLoader={() => (
														<ButtonDropdownField disabled>
															<Text>{formatMessage({id: 'common.loading'})}</Text>
														</ButtonDropdownField>
													)}
													// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated query
													render={(relayProps: any) => {
														const projectOptions = relayProps.viewer.projects.edges
															.map((edge: {node: any}) => edge.node) // eslint-disable-line @typescript-eslint/no-explicit-any
															.filter(
																(node: {fullAccessToProject: boolean}) =>
																	node.fullAccessToProject
															);
														return (
															<DropdownList>
																<DropdownList.Trigger
																	data-cy="opportunity-duplicate-project"
																	aria-label={formatMessage({
																		id: 'opportunity.project_to_duplicate',
																	})}
																>
																	<ButtonDropdownField>
																		<Text>
																			{projectToDuplicate
																				? `${projectToDuplicate.customProjectId} ${projectToDuplicate.name}`
																				: formatMessage({id: 'common.none'})}
																		</Text>
																	</ButtonDropdownField>
																</DropdownList.Trigger>
																<DropdownList.Content align="start" useTriggerWidth>
																	<DropdownList.Item
																		data-cy="opportunity-duplicate-project-none"
																		onClick={() => handleDuplicateProjectSelectChange(null)}
																	>
																		<Text>{formatMessage({id: 'common.none'})}</Text>
																	</DropdownList.Item>
																	{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
																	{projectOptions.map((node: any) => (
																		<DropdownList.Item
																			key={node.id}
																			data-cy={`opportunity-duplicate-project-${node.id}`}
																			onClick={() =>
																				handleDuplicateProjectSelectChange(node)
																			}
																		>
																			<Text>{`${node.customProjectId} ${node.name}`}</Text>
																		</DropdownList.Item>
																	))}
																</DropdownList.Content>
															</DropdownList>
														);
													}}
												/>
											</FlexColumn>
										</div>
									</FlexItem>
								</FlexRow>
								{projectToDuplicate ? (
									<FlexColumn gap="s">
										<Label>{formatMessage({id: 'common.duplicate'})}:</Label>
										{/* Options grouped into columns. "Deliverables" is omitted — DuplicateProjectInput
has no duplicateDeliverables field (deliverables copy with Phases/baseline). */}
										<FlexRow gap="l" alignItems="flex-start">
											<FlexItem flex="1 1 0">
												<FlexColumn gap="xs">
													<Label>
														{formatMessage({id: 'opportunity.duplicate_group.structure'})}
													</Label>
													<Checkbox
														checked={duplicateTasks}
														onChange={checked => setDuplicateTasks(!!checked)}
														labelText={formatMessage({id: 'opportunity.duplicate_item.tasks'})}
													/>
													<Checkbox
														checked={duplicatePhases}
														onChange={checked => setDuplicatePhases(!!checked)}
														labelText={formatMessage({id: 'opportunity.duplicate_item.phases'})}
													/>
												</FlexColumn>
											</FlexItem>
											<FlexItem flex="1 1 0">
												<FlexColumn gap="xs">
													<Label>
														{formatMessage({id: 'opportunity.duplicate_group.resourcing'})}
													</Label>
													<Checkbox
														checked={duplicateTeam}
														onChange={checked => setDuplicateTeam(!!checked)}
														labelText={formatMessage({
															id: 'opportunity.duplicate_item.team_members',
														})}
													/>
													{duplicateTeam ? (
														<Checkbox
															checked={duplicateAllocations}
															onChange={checked => setDuplicateAllocations(!!checked)}
															labelText={formatMessage({
																id: 'opportunity.duplicate_item.allocations',
															})}
														/>
													) : null}
													{hasModule(MODULE_TYPES.PLACEHOLDERS) ? (
														<Checkbox
															checked={duplicatePlaceholders}
															onChange={checked => setDuplicatePlaceholders(!!checked)}
															labelText={formatMessage({
																id: 'opportunity.duplicate_item.placeholders',
															})}
														/>
													) : null}
												</FlexColumn>
											</FlexItem>
											{CompanySetupUtil.hasFinance() ? (
												<FlexItem flex="1 1 0">
													<FlexColumn gap="xs">
														<Label>
															{formatMessage({id: 'opportunity.duplicate_group.financials'})}
														</Label>
														<Checkbox
															checked={duplicateExpenses}
															onChange={checked => setDuplicateExpenses(!!checked)}
															labelText={formatMessage({id: 'project_budget.expenses'})}
														/>
														<Checkbox
															checked={duplicateRetainerPeriods}
															onChange={checked => setDuplicateRetainerPeriods(!!checked)}
															labelText={formatMessage({
																id: 'opportunity.duplicate_item.retainer_periods',
															})}
														/>
													</FlexColumn>
												</FlexItem>
											) : null}
											<FlexItem flex="1 1 0">
												<FlexColumn gap="xs">
													<Label>{formatMessage({id: 'opportunity.duplicate_group.setup'})}</Label>
													<Checkbox
														checked={duplicateSettings}
														onChange={checked => setDuplicateSettings(!!checked)}
														labelText={formatMessage({id: 'duplicate-modal.settings-v2'})}
													/>
													<Checkbox
														checked={duplicateColor}
														onChange={checked => setDuplicateColor(!!checked)}
														labelText={formatMessage({
															id: 'integration.project_templates.project_color',
														})}
													/>
												</FlexColumn>
											</FlexItem>
										</FlexRow>
									</FlexColumn>
								) : null}
							</FlexColumn>
						) : null}
					</FlexColumn>
				</OpportunitySection>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleCreate}
					dataCy="create-opportunity-submit"
					disabled={!name.trim() || creating || !!customProjectIdError || colorInvalid || duplicateSourceMissing}
				>
					{formatMessage({id: 'opportunity.create_button'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="modal-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

const createOpportunityModalQuery = graphql`
	query CreateOpportunityModal_Query {
		viewer {
			actualPersonId
			component(name: "create_opportunity_modal")
			company {
				id
				projectLastSequence
				currency
				opportunityStageBands(first: 1000) {
					edges {
						node {
							id
							name
							startPercentage
							endPercentage
							winProbability
						}
					}
				}
				defaultRateCard {
					id
					name
					currency
				}
				rateCards(first: 10000) {
					edges {
						node {
							id
							name
							currency
						}
					}
				}
				clients(first: 10000) {
					edges {
						node {
							id
							name
						}
					}
				}
				allPersons(first: 10000, onlyActive: true, excludeClientUsers: true) {
					edges {
						node {
							id
							fullName
							active
						}
					}
				}
				labels(first: 10000, labelType: PROJECT) {
					edges {
						node {
							id
							name
							color
						}
					}
				}
				modules {
					moduleType
				}
			}
		}
	}
`;

const CreateOpportunityModal = (
	props: {closeModal: () => void} & Pick<CreateOpportunityModalPayload, 'clientId' | 'entryPoint' | 'onCreated'>
) => (
	<ForecastQueryRenderer
		key="query-render-createOpportunityModal"
		query={createOpportunityModalQuery}
		modal={true}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the modal's query is untyped, so the viewer shape is not narrowed here
		render={(relayProps: any) => (
			<CreateOpportunityModalInner
				viewer={relayProps.viewer}
				closeModal={props.closeModal}
				clientId={props.clientId}
				entryPoint={props.entryPoint}
				onCreated={props.onCreated}
			/>
		)}
	/>
);

export default CreateOpportunityModal;
